"use client";

import Image from "next/image";
import { useTranslations } from "next-intl";
import { Plus, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { useCallback, useState } from "react";
import { toast } from "sonner";
import { CreateCollectionDialog } from "@/components/my-listings/create-collection-dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogBody,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { resolveStorageUrl } from "@/lib/storage/public-url";

export type ProfileCollectionHighlight = {
  id: string;
  title: string;
  listings: { image_urls?: string[] | null }[];
};

type ProfileCollectionHighlightsProps = {
  collections: ProfileCollectionHighlight[];
  isOwner?: boolean;
  sellerBanned?: boolean;
  onSelectCollection: (collectionId: string) => void;
  onCollectionCreated?: (collection: ProfileCollectionHighlight) => void;
  onCollectionDeleted?: (collectionId: string) => void | Promise<void>;
  className?: string;
};

// Resolved here: `HighlightCircle` renders this through next/image, which
// throws on a bucket-relative `listings.image_urls` entry.
function collectionPreviewUrl(collection: ProfileCollectionHighlight): string | null {
  for (const listing of collection.listings) {
    const urls = listing.image_urls;
    if (Array.isArray(urls) && urls.length > 0 && urls[0]) {
      return resolveStorageUrl(urls[0]);
    }
  }
  return null;
}

function HighlightCircle({
  label,
  imageUrl,
  onClick,
  onDelete,
  ariaLabel,
  deleteAriaLabel,
  variant = "collection",
}: {
  label: string;
  imageUrl?: string | null;
  onClick: () => void;
  onDelete?: () => void;
  ariaLabel: string;
  deleteAriaLabel?: string;
  variant?: "collection" | "new";
}) {
  return (
    <div className="group flex w-[72px] shrink-0 flex-col items-center gap-1.5 text-center">
      <div className="relative">
        <button
          type="button"
          onClick={onClick}
          className="rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          aria-label={ariaLabel}
        >
          <div
            className={cn(
              "relative h-[68px] w-[68px] rounded-full p-[2px]",
              variant === "collection"
                ? "bg-linear-to-br from-border via-muted-foreground/30 to-border"
                : "bg-border",
            )}
          >
            <div
              className={cn(
                "flex h-full w-full items-center justify-center overflow-hidden rounded-full border border-background",
                variant === "new" ? "bg-muted" : "bg-muted",
              )}
            >
              {variant === "new" ? (
                <Plus className="h-6 w-6 text-muted-foreground" aria-hidden />
              ) : imageUrl ? (
                <div className="relative h-full w-full">
                  <Image
                    src={imageUrl}
                    alt=""
                    fill
                    className="object-cover"
                    sizes="68px"
                  />
                </div>
              ) : (
                <span className="text-lg font-semibold text-muted-foreground">
                  {label.charAt(0).toUpperCase()}
                </span>
              )}
            </div>
          </div>
        </button>
        {onDelete ? (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onDelete();
            }}
            className="absolute -right-0.5 -top-0.5 z-10 flex h-5 w-5 items-center justify-center rounded-full border border-background bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label={deleteAriaLabel}
          >
            <Trash2 className="h-2.5 w-2.5" aria-hidden />
          </button>
        ) : null}
      </div>
      <span className="max-w-[72px] truncate text-[11px] font-medium text-foreground">
        {label}
      </span>
    </div>
  );
}

export function ProfileCollectionHighlights({
  collections,
  isOwner = false,
  sellerBanned = false,
  onSelectCollection,
  onCollectionCreated,
  onCollectionDeleted,
  className,
}: ProfileCollectionHighlightsProps) {
  const tTabs = useTranslations("profile.tabs");
  const tColl = useTranslations("collections");
  const tProfile = useTranslations("profile");
  const tCommon = useTranslations("common");
  const [createOpen, setCreateOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const showNewButton = isOwner && !sellerBanned && onCollectionCreated;
  const showDeleteButton = isOwner && !sellerBanned && onCollectionDeleted;

  const handleCreateCollection = useCallback(
    async (title: string, description: string, _listingIds: string[]) => {
      if (isCreating) return;

      setIsCreating(true);
      try {
        const response = await fetch("/api/collections", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            title,
            description: description || undefined,
          }),
        });
        if (!response.ok) {
          const payload = await response.json().catch(() => null);
          throw new Error(payload?.error ?? "Failed to create collection");
        }
        const payload = await response.json();
        const created = payload.collection ?? payload;
        onCollectionCreated?.({
          id: created.id,
          title: created.title,
          listings: [],
        });
        toast.success(tColl("createBtn"));
      } catch (error) {
        toast.error(error instanceof Error ? error.message : tColl("createBtn"));
      } finally {
        setIsCreating(false);
      }
    },
    [isCreating, onCollectionCreated, tColl],
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteConfirmId || !onCollectionDeleted) return;
    setIsDeleting(true);
    try {
      await onCollectionDeleted(deleteConfirmId);
      setDeleteConfirmId(null);
    } finally {
      setIsDeleting(false);
    }
  }, [deleteConfirmId, onCollectionDeleted]);

  if (collections.length === 0 && !showNewButton) {
    return null;
  }

  return (
    <>
      <div className={cn("w-full overflow-hidden", className)}>
        <div className="flex gap-3 overflow-x-auto scrollbar-hide pb-1 pt-0.5">
          {collections.map((collection) => (
            <HighlightCircle
              key={collection.id}
              label={collection.title}
              imageUrl={collectionPreviewUrl(collection)}
              onClick={() => onSelectCollection(collection.id)}
              onDelete={
                showDeleteButton
                  ? () => setDeleteConfirmId(collection.id)
                  : undefined
              }
              ariaLabel={`${tTabs("viewAll")} ${collection.title}`}
              deleteAriaLabel={tColl("deleteBtn")}
            />
          ))}
          {showNewButton ? (
            <HighlightCircle
              label={tTabs("newCollection")}
              variant="new"
              onClick={() => setCreateOpen(true)}
              ariaLabel={tColl("createTitle")}
            />
          ) : null}
        </div>
      </div>

      {showNewButton ? (
        <CreateCollectionDialog
          selectedListingIds={[]}
          allowEmptySelection
          hideTrigger
          open={createOpen}
          onOpenChange={setCreateOpen}
          onCreateCollectionAction={handleCreateCollection}
          isCreating={isCreating}
        />
      ) : null}

      {showDeleteButton ? (
        <AlertDialog
          open={!!deleteConfirmId}
          onOpenChange={(open) => !open && !isDeleting && setDeleteConfirmId(null)}
        >
          <AlertDialogContent className="w-[calc(100vw-2rem)] sm:w-full">
            <AlertDialogHeader>
              <AlertDialogTitle>{tProfile("deleteCollection")}</AlertDialogTitle>
            </AlertDialogHeader>
            <AlertDialogBody>
              <AlertDialogDescription>{tColl("deleteConfirmFull")}</AlertDialogDescription>
            </AlertDialogBody>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isDeleting}>{tCommon("cancel")}</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => void handleDeleteConfirm()}
                disabled={isDeleting}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {tColl("deleteBtn")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      ) : null}
    </>
  );
}
